import { useEffect, useState } from "react";
import { FilterValues } from "../../../data-model/filterValues";
import {
  buildWorklistFetchPayload,
  PharmaStpFileFilterPayload,
} from "../utils/buildWorklistFetchPayload";
import { CallingWorklistView } from "../utils/buildWorklistFilters";

export type { PharmaStpFileFilterPayload };

export interface UseFilterPayloadReturn {
  filterPayload: PharmaStpFileFilterPayload | null;
  setFilterPayload: React.Dispatch<
    React.SetStateAction<PharmaStpFileFilterPayload | null>
  >;
}

/**
 * Builds a refetch payload whenever filters or search change after the initial load.
 *
 * Includes DOW `date_from_filename_*` bounds when the user has selected a date
 * range so the table actually narrows to that window.
 *
 * @input validFilters - Filter panel selections for this table
 * @input searchTerm - MRN search box contents
 * @input initialDataFetchingExecuted - False until the first page has loaded
 * @input pageSize - Rows per page
 * @input isArchiveView - When true, payloads use archive list filters
 * @input view - Worklist the payload is for
 * @return Current filter payload and its setter
 */
export const useFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  pageSize: number = 120,
  /** When true, payloads use archive list filters (same as archive icon). */
  isArchiveView: boolean = false,
  /** Worklist the payload is for; the Pending list also filters on response status. */
  view: CallingWorklistView = CallingWorklistView.NON_EPA,
): UseFilterPayloadReturn => {
  const [filterPayload, setFilterPayload] =
    useState<PharmaStpFileFilterPayload | null>(null);

  useEffect(() => {
    if (!initialDataFetchingExecuted) {
      setFilterPayload(null);
    } else {
      setFilterPayload(
        buildWorklistFetchPayload({
          view,
          isArchiveView,
          validFilters,
          searchTerm,
          pageSize,
        }),
      );
    }
  }, [
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    pageSize,
    isArchiveView,
    view,
  ]);

  return {
    filterPayload,
    setFilterPayload,
  };
};
